import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Button,
  Checkbox,
  Empty,
  Form,
  Input,
  Segmented,
  Select,
  Skeleton,
  Tooltip,
  Typography,
  message,
  theme,
} from "antd";
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  CloseOutlined,
  HolderOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import {
  AggregationFn,
  ApiService,
  Report,
  ReportColumn,
  ReportColumns,
  ReportMetric,
  Template,
} from "../../api";
import { useTabTitle } from "../../tabs/tabContext";
import { Divider, Field, Page, PageHeader, Panel, SectionLabel, space } from "./ui";

const { Text } = Typography;

const columnId = (column: ReportColumn) => `${column.source}:${column.key}`;

const AGGREGATIONS: AggregationFn[] = ["sum", "avg", "min", "max"];

/**
 * Defining what a report asks.
 *
 * The hard part is not the form, it is the column picker: answers live in
 * `RequestFormFieldValue` keyed by stored names like `work_location`, and
 * nobody outside the development team knows those. So the picker is driven by
 * the same schema walk the condition builder uses — it shows "Location", and
 * the stored name rides along underneath, in the muted step, for the one person
 * who does need to see it.
 *
 * The page is ordered the way the task is: name it, choose what goes in it,
 * then — optionally — decide how it rolls up. Grouping is last because most
 * reports never need it, and a builder that leads with aggregation makes a
 * simple list feel like a hard job.
 */
const ReportBuilderPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = theme.useToken();
  const isNew = !id;

  const [form] = Form.useForm();
  const [templates, setTemplates] = useState<Template[]>([]);
  const [templateId, setTemplateId] = useState<number | undefined>();
  const [catalogue, setCatalogue] = useState<ReportColumns | undefined>();
  const [statuses, setStatuses] = useState<any[]>([]);
  const [columns, setColumns] = useState<ReportColumn[]>([]);
  const [groupMode, setGroupMode] = useState<"none" | "period" | "column">("none");
  const [groupKey, setGroupKey] = useState<string | undefined>();
  const [bucket, setBucket] = useState<"month" | "quarter" | "year">("month");
  const [metrics, setMetrics] = useState<ReportMetric[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useTabTitle(isNew ? t("New report") : t("Edit report"));

  useEffect(() => {
    (async () => {
      try {
        setTemplates(await ApiService.apiMainTemplatesList());
        if (!isNew) {
          const report: Report = await ApiService.apiMainReportsRetrieve(
            parseInt(id!)
          );
          form.setFieldsValue({
            name: report.name,
            description: report.description,
            template: report.template,
            period_field: report.period_field,
            statuses: report.statuses,
          });
          setTemplateId(report.template);
          setColumns(report.columns);
          const group = report.group_by as any;
          if (group?.source === "period") {
            setGroupMode("period");
            setBucket(group.bucket || "month");
          } else if (group?.source) {
            setGroupMode("column");
            setGroupKey(`${group.source}:${group.key}`);
          }
          setMetrics((report.metrics ?? []).filter((m) => m.fn !== "count"));
        }
      } catch (e: any) {
        message.error(e?.body?.message ?? t("Something went wrong"));
      }
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadTemplateData = useCallback(async (nextTemplateId: number) => {
    try {
      const [cols, statusList] = await Promise.all([
        ApiService.apiMainReportsColumns(nextTemplateId),
        ApiService.apiMainRequestStatusesByTemplateRetrieve(nextTemplateId),
      ]);
      setCatalogue(cols);
      setStatuses(statusList as any[]);
    } catch (e: any) {
      message.error(e?.body?.message ?? t("Something went wrong"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (templateId) loadTemplateData(templateId);
  }, [templateId, loadTemplateData]);

  const selected = useMemo(() => new Set(columns.map(columnId)), [columns]);

  /** Everything that can label a bucket — a set-valued field cannot. */
  const groupableOptions = useMemo(() => {
    if (!catalogue) return [];
    return [
      {
        label: t("The request itself"),
        options: catalogue.core.map((core) => ({
          value: `core:${core.key}`,
          label: t(core.title),
        })),
      },
      ...catalogue.sections
        .map((section) => ({
          label: section.title,
          options: section.fields
            .filter((field) => field.groupable !== false)
            .map((field) => ({
              value: `field:${field.name}`,
              label: field.label,
            })),
        }))
        .filter((group) => group.options.length > 0),
    ];
  }, [catalogue, t]);

  /** Only form fields can be summed — the numbers people type live there. */
  const measurableOptions = useMemo(() => {
    if (!catalogue) return [];
    return catalogue.sections
      .map((section) => ({
        label: section.title,
        options: section.fields.map((field) => ({
          value: field.name,
          label: field.label,
        })),
      }))
      .filter((group) => group.options.length > 0);
  }, [catalogue]);

  const toggle = (column: ReportColumn, checked: boolean) =>
    setColumns((current) =>
      checked
        ? [...current, column]
        : current.filter((c) => columnId(c) !== columnId(column))
    );

  const move = (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= columns.length) return;
    const next = [...columns];
    [next[index], next[target]] = [next[target], next[index]];
    setColumns(next);
  };

  const retitle = (index: number, title: string) => {
    const next = [...columns];
    // Empty means "use the form's own label", so a renamed field keeps a
    // sensible heading without the report being edited.
    next[index] = { ...next[index], title: title || undefined };
    setColumns(next);
  };

  const onTemplateChange = (value: number) => {
    if (columns.length && value !== templateId) {
      setColumns([]);
      setGroupMode("none");
      setGroupKey(undefined);
      setMetrics([]);
      message.info(t("Columns were cleared because the template changed."));
    }
    setTemplateId(value);
    form.setFieldsValue({ statuses: [] });
  };

  const save = async () => {
    const values = await form.validateFields();
    if (columns.length === 0) {
      message.error(t("Pick at least one column."));
      return;
    }
    if (groupMode === "column" && !groupKey) {
      message.error(t("Choose what to group by."));
      return;
    }
    setSaving(true);
    try {
      const [source, key] = (groupKey ?? "").split(":");
      const group_by =
        groupMode === "period"
          ? { source: "period", bucket }
          : groupMode === "column" && groupKey
          ? { source, key }
          : {};
      const saved = isNew
        ? await ApiService.apiMainReportsCreate({
            ...values,
            columns,
            group_by,
            metrics,
          } as any)
        : await ApiService.apiMainReportsPartialUpdate(parseInt(id!), {
            ...values,
            columns,
            group_by,
            metrics,
          } as any);
      message.success(t("Saved"));
      navigate(`/reports/${saved.id}`);
    } catch (e: any) {
      message.error(e?.body?.message ?? t("Something went wrong"));
    }
    setSaving(false);
  };

  const fieldLabel = (column: ReportColumn) => {
    if (column.source === "core") {
      return catalogue?.core.find((c) => c.key === column.key)?.title ?? column.key;
    }
    for (const section of catalogue?.sections ?? []) {
      const field = section.fields.find((f) => f.name === column.key);
      if (field) return field.label;
    }
    return column.key;
  };

  /** One tickable row in the left-hand picker. */
  const pickRow = (
    key: string,
    label: string,
    column: ReportColumn,
    hint?: string
  ) => (
    <label
      key={key}
      style={{
        display: "flex",
        alignItems: "center",
        gap: space.tight,
        padding: `6px ${space.tight}px`,
        borderRadius: token.borderRadius,
        cursor: "pointer",
      }}
      onMouseEnter={(e) =>
        (e.currentTarget.style.background = token.colorFillQuaternary)
      }
      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
    >
      <Checkbox
        checked={selected.has(columnId(column))}
        onChange={(e) => toggle(column, e.target.checked)}
      />
      <span style={{ flex: 1, minWidth: 0 }}>
        <span
          style={{
            display: "block",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {label}
        </span>
        {hint && (
          <span
            style={{
              display: "block",
              fontSize: 11,
              color: token.colorTextQuaternary,
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            }}
          >
            {hint}
          </span>
        )}
      </span>
    </label>
  );

  if (loading) {
    return (
      <>
        <Skeleton active paragraph={{ rows: 1 }} style={{ maxWidth: 420 }} />
        <Panel style={{ marginTop: space.base }}>
          <Skeleton active paragraph={{ rows: 6 }} />
        </Panel>
      </>
    );
  }

  return (
    <Form form={form} layout="vertical" initialValues={{ period_field: "start_date" }}>
      <Page>
      <PageHeader
        title={isNew ? t("New report") : t("Edit report")}
        meta={t("Pick a template, choose what goes in the columns, then how it adds up.")}
        actions={
          <>
            <Button onClick={() => navigate("/reports")}>{t("Cancel")}</Button>
            <Button type="primary" loading={saving} onClick={save}>
              {t("Save")}
            </Button>
          </>
        }
      />

      <Panel style={{ marginBottom: space.base }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: space.base,
          }}
        >
          <Form.Item
            name="name"
            label={t("Name")}
            rules={[{ required: true, message: t("A name is required.") }]}
            style={{ marginBottom: 0 }}
          >
            <Input placeholder={t("e.g. Permits closed this month")} />
          </Form.Item>
          <Form.Item
            name="template"
            label={t("Template")}
            rules={[{ required: true, message: t("Choose a template.") }]}
            style={{ marginBottom: 0 }}
          >
            <Select
              showSearch
              optionFilterProp="label"
              onChange={onTemplateChange}
              placeholder={t("Choose a template.")}
              options={templates.map((tpl) => ({ value: tpl.id, label: tpl.name }))}
            />
          </Form.Item>
          <Form.Item
            name="description"
            label={t("Description")}
            style={{ marginBottom: 0 }}
          >
            <Input placeholder={t("What question does this answer?")} />
          </Form.Item>
        </div>

        <Divider style={{ margin: `${space.base}px 0` }} />

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: space.base,
          }}
        >
          <Form.Item
            name="period_field"
            label={t("Measure the period against")}
            extra={t("A request records when the work runs, not when it was raised.")}
            style={{ marginBottom: 0 }}
          >
            <Select
              options={[
                { value: "start_date", label: t("Start date") },
                { value: "end_date", label: t("End date") },
              ]}
            />
          </Form.Item>
          <Form.Item
            name="statuses"
            label={t("Limit to statuses")}
            extra={t("Leave empty for every status.")}
            style={{ marginBottom: 0 }}
          >
            <Select
              mode="multiple"
              allowClear
              maxTagCount="responsive"
              disabled={!templateId}
              optionFilterProp="label"
              placeholder={t("Every status")}
              options={statuses.map((s: any) => ({ value: s.id, label: s.name }))}
            />
          </Form.Item>
        </div>
      </Panel>

      <Panel padding={0} style={{ marginBottom: space.base }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
            alignItems: "stretch",
          }}
        >
          <div style={{ padding: space.base, minWidth: 0 }}>
            <SectionLabel style={{ marginBottom: space.tight }}>
              {t("Available columns")}
            </SectionLabel>
            {!catalogue ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={t("Choose a template to see what it can report on.")}
              />
            ) : (
              <div style={{ maxHeight: 460, overflowY: "auto", paddingRight: 4 }}>
                <SectionLabel
                  style={{ margin: `${space.tight}px 0 ${space.hair}px` }}
                >
                  {t("The request itself")}
                </SectionLabel>
                {catalogue.core.map((core) =>
                  pickRow(core.key, t(core.title), {
                    source: "core",
                    key: core.key,
                  })
                )}
                {catalogue.sections.map((section) => (
                  <div key={section.form}>
                    <SectionLabel
                      style={{ margin: `${space.snug}px 0 ${space.hair}px` }}
                    >
                      {section.title}
                    </SectionLabel>
                    {section.fields.length === 0 ? (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {t("This section has no fields yet.")}
                      </Text>
                    ) : (
                      section.fields.map((field) =>
                        pickRow(
                          `${section.form}-${field.name}`,
                          field.label,
                          { source: "field", key: field.name },
                          field.label === field.name ? undefined : field.name
                        )
                      )
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* A single hairline between the panes — two bordered boxes here
              would draw the same edge twice. */}
          <div
            style={{
              padding: space.base,
              minWidth: 0,
              borderLeft: `1px solid ${token.colorSplit}`,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
                marginBottom: space.tight,
              }}
            >
              <SectionLabel>{t("Columns in this report")}</SectionLabel>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {columns.length}
              </Text>
            </div>

            {columns.length === 0 ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {t("Tick a field on the left to add a column.")}
                  </Text>
                }
              />
            ) : (
              <div style={{ maxHeight: 460, overflowY: "auto" }}>
                {columns.map((column, index) => (
                  <div
                    key={columnId(column)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: space.tight,
                      padding: `4px 0`,
                    }}
                  >
                    <HolderOutlined
                      style={{ color: token.colorTextQuaternary, fontSize: 12 }}
                    />
                    <Input
                      size="small"
                      bordered={false}
                      value={column.title ?? ""}
                      placeholder={fieldLabel(column)}
                      onChange={(e) => retitle(index, e.target.value)}
                      style={{ paddingInline: 0 }}
                    />
                    <Tooltip title={t("Move up")}>
                      <Button
                        size="small"
                        type="text"
                        disabled={index === 0}
                        icon={<ArrowUpOutlined />}
                        onClick={() => move(index, -1)}
                      />
                    </Tooltip>
                    <Tooltip title={t("Move down")}>
                      <Button
                        size="small"
                        type="text"
                        disabled={index === columns.length - 1}
                        icon={<ArrowDownOutlined />}
                        onClick={() => move(index, 1)}
                      />
                    </Tooltip>
                    <Button
                      size="small"
                      type="text"
                      danger
                      icon={<CloseOutlined />}
                      onClick={() => toggle(column, false)}
                    />
                  </div>
                ))}
                <Text
                  type="secondary"
                  style={{ fontSize: 12, display: "block", marginTop: space.tight }}
                >
                  {t("Columns appear left to right in this order. Type to rename a heading.")}
                </Text>
              </div>
            )}
          </div>
        </div>
      </Panel>

      <Panel>
        <SectionLabel style={{ marginBottom: space.hair }}>
          {t("Roll-up")}
        </SectionLabel>
        <Text type="secondary" style={{ fontSize: 13 }}>
          {t("Optional. Adds totals and a chart above the rows.")}
        </Text>

        <div style={{ marginTop: space.snug, display: "flex", gap: space.base, flexWrap: "wrap", alignItems: "flex-end" }}>
          <Field label={t("Group by")}>
            <Segmented
              value={groupMode}
              onChange={(value) => setGroupMode(value as any)}
              options={[
                { label: t("Nothing"), value: "none" },
                { label: t("Time"), value: "period" },
                { label: t("A column"), value: "column" },
              ]}
            />
          </Field>

          {groupMode === "period" && (
            <Field label={t("Bucket")}>
              <Select
                style={{ width: 148 }}
                value={bucket}
                onChange={setBucket}
                options={[
                  { value: "month", label: t("Month") },
                  { value: "quarter", label: t("Quarter") },
                  { value: "year", label: t("Year") },
                ]}
              />
            </Field>
          )}

          {groupMode === "column" && (
            <Field label={t("Column")} style={{ flex: "1 1 260px", minWidth: 220 }}>
              <Select
                showSearch
                optionFilterProp="label"
                value={groupKey}
                onChange={setGroupKey}
                placeholder={t("Choose what to group by.")}
                options={groupableOptions}
              />
            </Field>
          )}
        </div>

        {groupMode !== "none" && (
          <>
            <Divider style={{ margin: `${space.base}px 0` }} />
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
                marginBottom: space.tight,
              }}
            >
              <SectionLabel>{t("Measures")}</SectionLabel>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t("How many requests is always counted.")}
              </Text>
            </div>

            {metrics.map((metric, index) => (
              <div
                key={index}
                style={{
                  display: "flex",
                  gap: space.tight,
                  alignItems: "center",
                  marginBottom: space.tight,
                }}
              >
                <Select
                  style={{ width: 120 }}
                  value={metric.fn}
                  onChange={(value) =>
                    setMetrics((current) =>
                      current.map((m, i) => (i === index ? { ...m, fn: value } : m))
                    )
                  }
                  options={AGGREGATIONS.map((fn) => ({
                    value: fn,
                    label: t(fn === "avg" ? "Average" : fn[0].toUpperCase() + fn.slice(1)),
                  }))}
                />
                <Text type="secondary" style={{ fontSize: 13 }}>
                  {t("of")}
                </Text>
                <Select
                  showSearch
                  optionFilterProp="label"
                  style={{ flex: "1 1 240px", maxWidth: 340 }}
                  value={metric.key}
                  placeholder={t("Choose a field")}
                  onChange={(value) =>
                    setMetrics((current) =>
                      current.map((m, i) => (i === index ? { ...m, key: value } : m))
                    )
                  }
                  options={measurableOptions}
                />
                <Button
                  type="text"
                  danger
                  icon={<CloseOutlined />}
                  onClick={() =>
                    setMetrics((current) => current.filter((_, i) => i !== index))
                  }
                />
              </div>
            ))}

            <Button
              type="dashed"
              size="small"
              icon={<PlusOutlined />}
              onClick={() =>
                setMetrics((current) => [...current, { fn: "sum", key: "" }])
              }
            >
              {t("Add a measure")}
            </Button>
            <div style={{ marginTop: space.tight }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t("Answers that aren't numbers are skipped, not counted as zero.")}
              </Text>
            </div>
          </>
        )}
      </Panel>
      </Page>
    </Form>
  );
};

export default ReportBuilderPage;
